import './crypto'

const encoder = new TextEncoder();

let inWorkPromise = null;
function hasLeadingZeroBits(hash, bits) {
    bits = Number(bits);

    const wholeBytes = Math.floor(bits / 8);

    for (let i = 0; i < wholeBytes; i++) {
        if (hash[i] !== 0) {
            return false;
        }
    }

    const remainingBits = bits % 8;

    return remainingBits === 0 || (hash[wholeBytes] & (0xff << (8 - remainingBits))) === 0;
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function getsha256(input) {
    const data = encoder.encode(input);

    // Modern Browser
    if (window.crypto && window.crypto.subtle) {
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        return new Uint8Array(hashBuffer);
    }

    // Fallback for older Browser
    const hashHex = sha256(input);

    // Hex-String -> Uint8Array
    const bytes = new Uint8Array(hashHex.length / 2);

    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hashHex.substr(i * 2, 2), 16);
    }

    return bytes;
}
function createApi(baseUrl) {
    baseUrl = baseUrl.replace(/\/$/, '');

    return {
        async refresh() {
            const response = await fetch(`${baseUrl}/api/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    flow: 'guest',
                }),
            });

            if (!response.ok) {
                throw new Error(`Refresh failed (${response.status})`);
            }

            return response.json();
        },
        async getChallenge() {
            const response = await fetch(`${baseUrl}/api/auth/powchallenge`, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch challenge (${response.status})`);
            }

            return response.json();
        },

        async solve(token, counter) {
            const response = await fetch(`${baseUrl}/api/auth/powchallenge/resolve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    counter: String(counter),
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to resolve challenge (${response.status})`);
            }

            return response.json();
        },
    };
}

async function solveProofOfWork(api) {
    let challenge = await api.getChallenge();

    let { nonce, username, difficulty, token } = challenge;

    const maxDuration = 3500;

    let counter = 0;
    let start = performance.now();

    while (true) {
        for (let i = 0; i < 1000; i++) {
            const hash = await getsha256(`${nonce}:${username}:${counter}`);

            if (hasLeadingZeroBits(hash, difficulty)) {
                return {
                    token,
                    counter,
                };
            }

            counter++;
        }

        await sleep(0);

        if (performance.now() - start > maxDuration) {
            const freshChallenge = await api.getChallenge();

            nonce = freshChallenge.nonce;
            username = freshChallenge.username;
            difficulty = freshChallenge.difficulty;
            token = freshChallenge.token;

            counter = 0;
            start = performance.now();
        }
    }
}

/**
 * Public API
 *
 * Only one authentication process can run at the same time.
 * Parallel calls receive the same Promise.
 *
 * @param {string} baseUrl
 * @returns {Promise<string>}
 */
export function solveChallenge(baseUrl) {
    if (inWorkPromise) {
        return inWorkPromise;
    }

    inWorkPromise = (async () => {
        try {
            const api = createApi(baseUrl);

            // First try guest refresh
            const refreshResult = await api.refresh().catch(() => null);

            // Refresh already returned a token
            if (refreshResult?.token) {
                return refreshResult.token;
            }

            const solution = await solveProofOfWork(api);

            const result = await api.solve(solution.token, solution.counter);

            if (!result?.token) {
                throw new Error('Server did not return a valid token');
            }

            return result.token;
        } catch (error) {
            throw new Error(`Proof-of-work authentication failed: ${error.message}`);
        } finally {
            // Allow next authentication attempt
            inWorkPromise = null;
        }
    })();

    return inWorkPromise;
}

